/**
 * S-92 ②: `tenantExportData` ページング全件取得の共通ロジック。
 *
 * apps/tenant-reservation と apps/saas-admin に byte-identical で複製されていた
 * ~220 行を 1 本化したもの（片側だけ直る drift の封鎖）。**Firebase 非依存** —
 * Callable の発行は `callPage` として注入する（`createCollectionAllPagesFetcher`）。
 * これによりテストも本ファイル併設の 1 本に統合され、フェイク注入だけで検証できる。
 *
 * 由来: S-84（ページング導入）/ S-88（リトライ・予算）/ S-92（中断・時刻・非送出防御）。
 */
// ---------- 定数 ----------
/** S-84 review: 画面ページングのページ数上限。超過 = 画面では取り切れず CLI フォールバックへ。 */
const MAX_EXPORT_PAGES = 10_000;
/** S-88: ページ取得の最大試行回数（初回含む）。 */
const PAGE_FETCH_MAX_ATTEMPTS = 3;
/** S-88: リトライの初回待機 ms（指数バックオフ: 500 → 1000）。 */
const PAGE_RETRY_BASE_DELAY_MS = 500;
/** S-88: リトライしてよい transient なエラーコード（権限・入力エラーは即時失敗させる）。 */
const RETRYABLE_FUNCTIONS_CODES = new Set([
    'functions/unavailable',
    'functions/deadline-exceeded',
    'functions/internal',
    'functions/resource-exhausted',
]);
/**
 * S-88: ブラウザ側で保持する取得データの概算ヒープ上限。JS 文字列は UTF-16（約 2 byte/文字）
 * のため「JSON 文字列長 × 2」で概算する。CSV 化フェーズでは rows に加えて CSV 文字列
 * （toCsvParts）と Blob が同時に常駐し、実ピークは概算の 2〜3 倍になるため（S-88 review）、
 * 上限はその分を織り込んで保守的に取る。超過時は無言のタブクラッシュ（OOM）ではなく
 * 明示 throw で CLI エクスポート（RUNBOOK §6.3c）へ誘導する（MAX_EXPORT_PAGES と同じ方針）。
 */
const CLIENT_EXPORT_BUDGET_BYTES = 128 * 1024 * 1024;
// ---------- 中断（AbortSignal / S-92） ----------
/**
 * S-92: 中断エラーの `name`。`AbortSignal.reason` の既定値である
 * `DOMException('...','AbortError')` と揃えることで、呼び出し側は `isExportAbortError`
 * 一本で自前 throw と標準 abort の両方を判別できる。
 */
const ABORT_ERROR_NAME = 'AbortError';
/**
 * S-92 ② 抽出時に標準 API へ寄せた: 中断エラーは独自 class ではなく
 * `AbortSignal.reason`（標準 / abort() 既定で `DOMException('AbortError')`）を第一とし、
 * reason が Error でない環境だけ `name='AbortError'` の Error にフォールバックする。
 */
function abortError(signal) {
    const reason = signal ? signal.reason : undefined;
    if (reason instanceof Error)
        return reason;
    const err = new Error('エクスポートの取得を中断しました。');
    err.name = ABORT_ERROR_NAME;
    return err;
}
/**
 * S-92: 中断由来のエラーか。ユーザー操作 / unmount による中断を「失敗」として
 * エラー表示しないための判定に使う。
 */
export function isExportAbortError(err) {
    return err instanceof Error && err.name === ABORT_ERROR_NAME;
}
function throwIfAborted(signal) {
    if (signal?.aborted)
        throw abortError(signal);
}
/**
 * S-92: リトライ待機。中断シグナルで待機自体を打ち切る（打ち切らないと、キャンセル後も
 * 最大 1.5 秒/ページの待機が残ページ分だけ空回りする）。
 */
function sleep(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(abortError(signal));
            return;
        }
        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        // 関数宣言（巻き上げ）で timer との相互参照を成立させる。
        function onAbort() {
            clearTimeout(timer);
            reject(abortError(signal));
        }
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}
function isFunctionsErrorLike(err) {
    return (typeof err === 'object' &&
        err !== null &&
        'code' in err &&
        typeof err.code === 'string' &&
        err.code.startsWith('functions/'));
}
function isRetryablePageError(err) {
    return isFunctionsErrorLike(err) && RETRYABLE_FUNCTIONS_CODES.has(err.code);
}
/** エクスポートエラーを管理画面向け日本語メッセージに変換。 */
export function tenantExportErrorMessage(err) {
    if (isFunctionsErrorLike(err)) {
        switch (err.code) {
            case 'functions/invalid-argument':
                return `入力値に誤りがあります：${err.message ?? ''}`;
            case 'functions/permission-denied':
                return '権限がありません（tenant-admin / saas-admin 権限が必要です）。';
            case 'functions/unauthenticated':
                return 'セッションが切れました。再ログインしてください。';
            default:
                return `エクスポートに失敗しました（${err.code}）。`;
        }
    }
    if (err instanceof Error) {
        return `エクスポートに失敗しました：${err.message}`;
    }
    return 'エクスポートに失敗しました。';
}
// ---------- ページング全件取得 ----------
/**
 * S-88: 取得データのヒープ概算。docs は httpsCallable の JSON デコード済みレスポンス
 * のため stringify は throw しない（循環参照・BigInt を含み得ない）。
 */
function estimatePageHeapBytes(docs) {
    return JSON.stringify(docs).length * 2;
}
/**
 * S-88: 1 ページ取得を指数バックオフ付きでリトライする。途中ページの transient 失敗
 * （unavailable / deadline-exceeded 等）で取得済み全ページを破棄しないための安全網。
 * S-92: 中断シグナルはリトライ待機中にも効く。
 */
async function callPageWithRetry(callPage, input, signal) {
    let lastErr;
    for (let attempt = 0; attempt < PAGE_FETCH_MAX_ATTEMPTS; attempt++) {
        if (attempt > 0) {
            await sleep(PAGE_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), signal);
        }
        try {
            return await callPage(input);
        }
        catch (err) {
            // S-92: 分類を先に行う。permission-denied / unauthenticated のような恒久的な失敗は
            // 中断中でも握り潰さずそのまま投げる（中断は「まだ続けられたはずの再試行」を止める
            // ためのもので、恒久的な失敗の通知を消すためのものではない）。
            if (!isRetryablePageError(err))
                throw err;
            // transient 失敗 + 中断済みなら再試行せず中断として返す
            //（キャンセル後に「エクスポートに失敗しました」を出さない）。
            throwIfAborted(signal);
            lastErr = err;
        }
    }
    throw lastErr;
}
/**
 * S-84: 大規模コレクションを cursor ページングで全件取得し 1 配列に結合する fetcher を作る。
 * 全件モードで打ち切られた（partial）コレクションの CSV を画面から完結させるために使う。
 * 上限ページ数 / ブラウザ概算ヒープ予算を超えた場合は無言で末尾を欠落させず throw する
 * （CLI フォールバックに誘導）。ページ単位の transient 失敗は指数バックオフでリトライする（S-88）。
 * 中断は `signal` で行う（S-92）。
 */
export function createCollectionAllPagesFetcher(callPage) {
    return async function fetchCollectionAllPages(input) {
        const clientBudgetBytes = input.clientBudgetBytes ?? CLIENT_EXPORT_BUDGET_BYTES;
        const all = [];
        let approxHeapBytes = 0;
        let cursor;
        for (let i = 0; i < MAX_EXPORT_PAGES; i++) {
            // S-92: ページ境界の判定（次ページの発行を止める）。
            throwIfAborted(input.signal);
            // S-92 review: 値が `undefined` のキーでも Firebase の callable シリアライザ
            // （encode / mapValues）は `null` に変換して送るため、サーバ側の `.optional()` schema が
            // 弾く（1 ページ目から invalid-argument / S-96 で顕在化）。省略したいパラメータは
            // 「値を undefined にする」のではなく **キーごと積まない**。
            const page = await callPageWithRetry(callPage, {
                tenantId: input.tenantId,
                collection: input.collection,
                ...(cursor !== undefined ? { cursor } : {}),
                ...(input.pageSize !== undefined ? { pageSize: input.pageSize } : {}),
            }, input.signal);
            // S-92: レスポンス到着後にも判定する。ここを省くと、最終ページで完走した場合に
            // 中断済みでも結果を返してしまい、離脱後のダウンロード発火が残る。
            throwIfAborted(input.signal);
            all.push(...page.docs);
            approxHeapBytes += estimatePageHeapBytes(page.docs);
            // S-88: サーバの byte 予算は 1 レスポンス単位でしか効かないため、ブラウザ側の蓄積にも
            // 予算を設ける（無言 OOM ではなく明示失敗で CLI へ誘導）。判定を nextCursor の完走
            // return より先に置くのは意図的 — 最終ページで完走していても、後続の CSV 化フェーズが
            // ピークを倍増させるため予算超過分は成功させない。
            if (approxHeapBytes > clientBudgetBytes) {
                throw new Error(`コレクション「${input.collection}」が大きすぎてブラウザでは取り切れません` +
                    `（概算 ${Math.round(approxHeapBytes / (1024 * 1024))}MB）。` +
                    `CLI エクスポート（RUNBOOK §6.3c）を利用してください。`);
            }
            // S-92 ③: 完走した最終ページのサーバ時刻を返す（= ページング取得が終わった時点）。
            if (!page.nextCursor)
                return { rows: all, exportedAt: page.exportedAt };
            cursor = page.nextCursor;
        }
        // S-84 review: 上限到達 = 全件取り切れていない。末尾欠落 CSV を「全件」と誤認させないため明示失敗。
        throw new Error(`コレクション「${input.collection}」が大きすぎて画面から取り切れません（${MAX_EXPORT_PAGES} ページ上限）。CLI エクスポート（RUNBOOK §6.3c）を利用してください。`);
    };
}
//# sourceMappingURL=exportPaging.js.map